import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { TAgetNewRegisters } from "../../../services/brandAPI"
import { useNavigate } from "react-router-dom"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts"
import { Calendar, Building2 } from "lucide-react"
import { getErrorStatus, getErrorResponse } from "../../../types/error"

interface DailyRegistration {
  date: string
  brandCount: number
}

interface NewRegistrationsData {
  dailyRegistrations: DailyRegistration[]
  summary: {
    totalBrands: number
    dateRange: {
      start: string
      end: string
    }
  }
}

type PeriodType = "daily" | "weekly" | "monthly"

const NewRegistrations = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [data, setData] = useState<NewRegistrationsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dateStart, setDateStart] = useState<string>("")
  const [dateEnd, setDateEnd] = useState<string>("")
  const [period, setPeriod] = useState<PeriodType>("daily")

  useEffect(() => {
    dispatch(setPageTitle("Kayıt İstatistikleri"))
  }, [dispatch])

  useEffect(() => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    setDateEnd(endDate.toISOString().split("T")[0])
    setDateStart(startDate.toISOString().split("T")[0])
  }, [])

  const fetchData = async () => {
    if (!dateStart || !dateEnd) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await TAgetNewRegisters(dateStart, dateEnd)
      setData(response)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Veri yüklenirken hata oluştu: ${getErrorResponse(error) || "Bilinmeyen hata"}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (dateStart && dateEnd) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStart, dateEnd])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR")
  }

  const processDataForPeriod = (data: NewRegistrationsData, periodType: PeriodType) => {
    if (!data || !data.dailyRegistrations.length) {
      return []
    }

    const processedData = new Map<string, number>()

    data.dailyRegistrations.forEach((item) => {
      const date = new Date(item.date)
      let key: string

      switch (periodType) {
        case "weekly": {
          const startOfWeek = new Date(date)
          const day = date.getDay()
          const diff = date.getDate() - day + (day === 0 ? -6 : 1)
          startOfWeek.setDate(diff)
          key = startOfWeek.toISOString().split("T")[0]
          break
        }
        case "monthly": {
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`
          break
        }
        default:
          key = item.date
          break
      }

      processedData.set(key, (processedData.get(key) || 0) + item.brandCount)
    })

    return Array.from(processedData.entries())
      .map(([date, brandCount]) => ({ date, brandCount }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  const getPeriodLabel = (periodType: PeriodType) => {
    switch (periodType) {
      case "weekly":
        return "Haftalık"
      case "monthly":
        return "Aylık"
      default:
        return "Günlük"
    }
  }

  const formatDateForPeriod = (dateString: string, periodType: PeriodType) => {
    const date = new Date(dateString)

    switch (periodType) {
      case "weekly": {
        const endOfWeek = new Date(date)
        endOfWeek.setDate(date.getDate() + 6)
        return `${date.toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
        })} - ${endOfWeek.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" })}`
      }
      case "monthly": {
        return date.toLocaleDateString("tr-TR", { year: "numeric", month: "long" })
      }
      default:
        return date.toLocaleDateString("tr-TR")
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="panel">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Marka Yeni Kayıtları İstatistikleri
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Marka kayıtlarının günlük, haftalık ve aylık analizi
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Görünüm
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as PeriodType)}
                className="form-select mr-4"
              >
                <option value="daily">Günlük</option>
                <option value="weekly">Haftalık</option>
                <option value="monthly">Aylık</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Başlangıç Tarihi
              </label>
              <input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                className="form-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bitiş Tarihi
              </label>
              <input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="flex items-end">
              <button onClick={fetchData} disabled={loading} className="btn btn-primary">
                {loading ? "Yükleniyor..." : "Yenile"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="panel">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="panel bg-gradient-to-r from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Toplam Marka</p>
                <p className="text-3xl font-bold">{data.summary.totalBrands}</p>
              </div>
              <Building2 className="w-12 h-12 text-green-200" />
            </div>
          </div>

          <div className="panel bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">Tarih Aralığı</p>
                <p className="text-sm font-semibold">
                  {formatDate(data.summary.dateRange.start)} -{" "}
                  {formatDate(data.summary.dateRange.end)}
                </p>
              </div>
              <Calendar className="w-12 h-12 text-orange-200" />
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {data &&
        data.dailyRegistrations.length > 0 &&
        (() => {
          const processedData = processDataForPeriod(data, period)
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Line Chart */}
              <div className="panel">
                <h3 className="text-lg font-semibold mb-4">
                  {getPeriodLabel(period)} Marka Kayıt Trendi
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={processedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => formatDateForPeriod(value, period)}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value) => formatDateForPeriod(value, period)}
                      formatter={(value, _name) => [value, "Marka"]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="brandCount"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Marka"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Bar Chart */}
              <div className="panel">
                <h3 className="text-lg font-semibold mb-4">
                  {getPeriodLabel(period)} Marka Kayıt Dağılımı
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={processedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => formatDateForPeriod(value, period)}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value) => formatDateForPeriod(value, period)}
                      formatter={(value, _name) => [value, "Marka"]}
                    />
                    <Legend />
                    <Bar dataKey="brandCount" fill="#10b981" name="Marka" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )
        })()}

      {/* Data Table - Horizontal Layout */}
      {data &&
        data.dailyRegistrations.length > 0 &&
        (() => {
          const processedData = processDataForPeriod(data, period)
          return (
            <div className="panel">
              <h3 className="text-lg font-semibold mb-4">
                {getPeriodLabel(period)} Marka Kayıt Detayları
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">
                        Metrik
                      </th>
                      {processedData.map((item, index) => (
                        <th
                          key={index}
                          className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[100px]"
                        >
                          {formatDateForPeriod(item.date, period)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {/* Marka Sayısı Row */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-900 z-10">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          Marka Sayısı
                        </span>
                      </td>
                      {processedData.map((item, index) => (
                        <td key={index} className="px-3 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {item.brandCount}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )
        })()}

      {/* No Data Message */}
      {data && data.dailyRegistrations.length === 0 && (
        <div className="panel">
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Veri Bulunamadı
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Seçilen tarih aralığında kayıt verisi bulunmamaktadır.
            </p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="panel">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-pink-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Veriler yükleniyor...</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default NewRegistrations
